import {
  CartAddressInput,
  CartFragment,
  CustomerAddress,
  GetCustomerAddressesQuery,
} from '../../graphql/magento'
import { CartAddress, CustomerAddressFromQuery, FormValues, ValidFormValues } from './types'

// used with assertRequiredFields() to assert AddressFieldValues from Partial<AddressFieldValues>
export const requiredAddressFieldValues = [
  'city',
  'country_code',
  'firstname',
  'lastname',
  'postcode',
  'region',
  'street1',
  'telephone',
] as const

export const countryCodesWithMandatoryRegion = ['AU', 'CA', 'ES', 'MX', 'US']

export const fieldValuesFromCartAddress = (
  cartAddress: CartAddress,
  defaultValues: FormValues.Address = {},
): ValidFormValues.Address => {
  if (cartAddress) {
    const { region_id: regionId, code: regionCode, label: regionLabel } = cartAddress.region ?? {}

    return {
      city: cartAddress.city || '',
      country_code: cartAddress.country?.code || '',
      firstname: cartAddress.firstname || '',
      lastname: cartAddress.lastname || '',
      postcode: cartAddress.postcode || '',
      region: regionCode || regionLabel || '',
      // M2 returns 0 if region is not a defined option
      region_id: `${regionId || ''}`,
      street1: cartAddress.street?.[0] || '',
      street2: cartAddress.street?.[1] || '',
      telephone: cartAddress.telephone || '',
    }
  }
  return {
    city: '',
    country_code: '',
    firstname: '',
    lastname: '',
    postcode: '',
    region: '',
    street1: '',
    street2: '',
    telephone: '',
    ...defaultValues,
  }
}

export const fieldValuesFromCustomerAddress = (
  customerAddress: NonNullable<CustomerAddressFromQuery>,
): ValidFormValues.Address => {
  const {
    region_id: regionId,
    region_code: regionCode,
    region: regionLabel,
  } = customerAddress.region ?? {}

  return {
    city: customerAddress.city || '',
    country_code: customerAddress.country_code || '',
    firstname: customerAddress.firstname || '',
    lastname: customerAddress.lastname || '',
    postcode: customerAddress.postcode || '',
    region: regionCode || regionLabel || '',
    // M2 returns 0 if region is not a defined option
    region_id: `${regionId || ''}`,
    street1: customerAddress.street?.[0] || '',
    street2: customerAddress.street?.[1] || '',
    telephone: customerAddress.telephone || '',
  }
}

export const dropShipFieldValuesFromCart = (cart: CartFragment): ValidFormValues.DropShip => ({
  dropShip: cart.drop_ship || '',
})

export const cartAddressInputFromFieldValues = (
  fieldValues: ValidFormValues.Address,
): CartAddressInput => {
  const regionId = fieldValues.region_id ? Number(fieldValues.region_id) : null
  return {
    city: fieldValues.city.trim(),
    country_code: fieldValues.country_code,
    firstname: fieldValues.firstname.trim(),
    lastname: fieldValues.lastname.trim(),
    postcode: fieldValues.postcode.trim(),
    ...(regionId != null && regionId > 0
      ? { region_id: regionId }
      : {
          // M2 ignores any attempt to reset or nullify `region_id` when regions are not allowed
          // for a country
          region: fieldValues.region?.trim() || '',
        }),
    street: [
      fieldValues.street1.trim(),
      ...(fieldValues.street2 ? [fieldValues.street2.trim()] : []),
    ],
    telephone: fieldValues.telephone.trim(),
  }
}

export const lookupKeyFromAddress = (
  address: Pick<
    CustomerAddress,
    | 'city'
    | 'country_code'
    | 'firstname'
    | 'lastname'
    | 'postcode'
    | 'region'
    | 'street'
    | 'telephone'
  >,
  // select which field to use for region
  regionField: 'region' | 'region_code' = 'region_code',
): string =>
  [
    address.city || '',
    address.country_code || '',
    address.firstname || '',
    address.lastname || '',
    address.postcode || '',
    address.region?.[regionField] || '',
    address.street?.[0] || '',
    address.street?.[1] || '',
    address.telephone || '',
  ].join('\0')

export const lookupKeyFromAddressFieldValues = (fieldValues: FormValues.Address): string =>
  [
    fieldValues.city || '',
    fieldValues.country_code || '',
    fieldValues.firstname || '',
    fieldValues.lastname || '',
    fieldValues.postcode || '',
    fieldValues.region || '',
    fieldValues.street1 || '',
    fieldValues.street2 || '',
    fieldValues.telephone || '',
  ].join('\0')

export const shouldSaveInAddressBook = ({
  customerAddresses,
  fieldValues,
}: {
  customerAddresses: NonNullable<GetCustomerAddressesQuery['customer']>['addresses']
  fieldValues: ValidFormValues.Address
}): boolean => {
  const lookupKey = lookupKeyFromAddressFieldValues(fieldValues)
  const found = customerAddresses
    ? customerAddresses.some(
        (address) =>
          !!address &&
          (lookupKeyFromAddress(address, 'region_code') === lookupKey ||
            // may not be necessary since M2 seems to return manually entered regions in both
            // region_code and region, but let's be explicit
            lookupKeyFromAddress(address, 'region') === lookupKey),
      )
    : false
  return !found
}

export const areAddressFieldValuesEqual = (
  a: ValidFormValues.Address,
  b: ValidFormValues.Address,
): boolean => {
  return (
    a.city === b.city &&
    a.country_code === b.country_code &&
    a.firstname === b.firstname &&
    a.lastname === b.lastname &&
    a.postcode === b.postcode &&
    ((a.region_id == null && b.region_id == null && a.region === b.region) ||
      a.region_id === b.region_id) &&
    a.street1 === b.street1 &&
    a.street2 === b.street2 &&
    a.telephone === b.telephone
  )
}
